import { effect } from '@preact/signals';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  Crosshair,
  LogOut,
  Minus,
  Network,
  Plus,
  Search,
  ShieldCheck,
  User,
  X,
} from 'lucide-preact';
import { ApiClient } from '../api/client';
import { PublicApi } from '../api/publicApi';
import { authControlModel } from './customerControls';
import { appEnv } from './env';
import { publicDataStatusLabel, resolvePublicEvents } from './publicData';
import {
  clockState,
  commandPaletteOpenState,
  confidenceGraphOpenState,
  confidenceThresholdState,
  dataError,
  dataStatus,
  eventsState,
  filterState,
  filteredEventsState,
  mapResolutionState,
  mapTargetState,
  narrowPanelState,
  selectedEventState,
  selectedHexEventsState,
  selectedHexIdState,
  selectEvent,
  selectHex,
  sortState,
  sourcesState,
} from './appState';
import type { ChaocEvent, EventCategory, EventSort } from '../contracts/events';
import { ChaocMap } from '../map/ChaocMap';
import {
  acceptCurrentLegal,
  authErrorState,
  authStatusState,
  authUserState,
  initializeAuth,
  legalGateOpenState,
  openAccountPortal,
  openSignIn,
  refreshViewerProfile,
  signOut,
  viewerProfileErrorState,
  viewerProfileState,
  viewerProfileStatusState,
} from '../auth/session';
import { shouldRefreshViewerProfile } from '../auth/profileState';

const CATEGORIES: EventCategory[] = ['all', 'conflict', 'economy', 'politics', 'science', 'tech', 'climate'];
const SORTS: EventSort[] = ['activity', 'recency', 'confidence'];
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const NAMED_GEOS: Record<string, { lat: number; lng: number; zoom: number; label: string }> = {
  paris: { lat: 48.85, lng: 2.35, zoom: 10, label: 'Paris, FR' },
  london: { lat: 51.51, lng: -0.13, zoom: 10, label: 'London, UK' },
  'new york': { lat: 40.71, lng: -74.01, zoom: 10, label: 'New York, US' },
  tokyo: { lat: 35.68, lng: 139.7, zoom: 10, label: 'Tokyo, JP' },
  beijing: { lat: 39.92, lng: 116.39, zoom: 9, label: 'Beijing, CN' },
  moscow: { lat: 55.76, lng: 37.62, zoom: 9, label: 'Moscow, RU' },
  singapore: { lat: 1.35, lng: 103.82, zoom: 12, label: 'Singapore, SG' },
  cairo: { lat: 30.05, lng: 31.25, zoom: 10, label: 'Cairo, EG' },
  jerusalem: { lat: 31.77, lng: 35.22, zoom: 11, label: 'Jerusalem, IL' },
  'san francisco': { lat: 37.77, lng: -122.4, zoom: 11, label: 'San Francisco, US' },
};

const OSE_BY_CATEGORY: Record<string, Array<{ platform: string; name: string; price: string; change: string; up: boolean }>> = {
  conflict: [
    { platform: 'kalshi', name: 'Ceasefire holds through June 2026?', price: '42%', change: '+4', up: true },
    { platform: 'poly', name: 'Resolution before year end', price: '31%', change: '-2', up: false },
    { platform: 'fut', name: 'Brent crude geopolitical premium', price: '$84.10', change: '+0.9%', up: true },
  ],
  economy: [
    { platform: 'kalshi', name: 'Fed rate cut before September 2026?', price: '68%', change: '+3', up: true },
    { platform: 'stock', name: 'SPDR S&P 500 ETF', price: '$562.30', change: '-0.3%', up: false },
    { platform: 'fx', name: 'EUR/USD', price: '1.0887', change: '+0.08%', up: true },
  ],
  politics: [
    { platform: 'kalshi', name: 'Government survives no-confidence vote?', price: '71%', change: '+5', up: true },
    { platform: 'poly', name: 'Coalition collapse before 2027', price: '38%', change: '+3', up: true },
    { platform: 'stock', name: 'MSCI Europe ETF', price: '$54.20', change: '+0.6%', up: true },
  ],
  science: [
    { platform: 'kalshi', name: 'mRNA flu vaccine approval in 2026?', price: '62%', change: '+4', up: true },
    { platform: 'stock', name: 'Moderna', price: '$68.20', change: '+3.2%', up: true },
    { platform: 'opt', name: 'MRNA catalyst call', price: '$3.10', change: '+0.60%', up: true },
  ],
  tech: [
    { platform: 'kalshi', name: 'AI lab IPO before Dec 2026?', price: '34%', change: '+8', up: true },
    { platform: 'stock', name: 'Nasdaq-100 ETF', price: '$493.10', change: '+0.9%', up: true },
    { platform: 'crypto', name: 'Coinbase proxy', price: '$247.80', change: '-1.1%', up: false },
  ],
  climate: [
    { platform: 'kalshi', name: 'Major hurricane hits US Gulf coast 2026', price: '44%', change: '+1', up: true },
    { platform: 'stock', name: 'First Solar', price: '$168.40', change: '+2.1%', up: true },
    { platform: 'fut', name: 'Natural Gas climate volatility', price: '$2.84', change: '+3.1%', up: true },
  ],
};

function pct(value: number): number {
  return Math.round(value * 100);
}

function activityClass(activity: number): string {
  if (activity >= 0.7) return 'act-high';
  if (activity >= 0.4) return 'act-med';
  return 'act-low';
}

function describeError(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function focusableDescendants(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

function useDialogFocus<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusTimer = window.setTimeout(() => {
      const focusables = focusableDescendants(root);
      (focusables[0] || root).focus({ preventScroll: true });
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = focusableDescendants(root);
      if (!focusables.length) {
        event.preventDefault();
        root.focus({ preventScroll: true });
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    root.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      root.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus({ preventScroll: true });
    };
  }, []);

  return ref;
}

function createPublicApi(): PublicApi {
  return new PublicApi(new ApiClient({ baseUrl: appEnv.apiBaseUrl }));
}

export function App() {
  const publicApi = useMemo(createPublicApi, []);

  useEffect(() => {
    if (!appEnv.clerkPublishableKey) {
      void initializeAuth();
      return;
    }

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (idleWindow.requestIdleCallback) {
      const idleId = idleWindow.requestIdleCallback(() => void initializeAuth(), { timeout: 2000 });
      return () => idleWindow.cancelIdleCallback?.(idleId);
    }

    const timer = window.setTimeout(() => void initializeAuth(), 800);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const dispose = effect(() => {
      if (!shouldRefreshViewerProfile(authStatusState.value, viewerProfileStatusState.value)) return;
      void refreshViewerProfile();
    });
    return dispose;
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    dataStatus.value = 'loading';
    dataError.value = null;
    const fixturesPromise: Promise<ChaocEvent[]> =
      import.meta.env.DEV && appEnv.fixturesEnabled
        ? import('../test/fixtures/events').then(({ FALLBACK_EVENTS }) => FALLBACK_EVENTS)
        : Promise.resolve([]);

    Promise.allSettled([publicApi.listEvents(controller.signal), publicApi.listSources(controller.signal), fixturesPromise])
      .then(([eventsResult, sourcesResult, fixturesResult]) => {
        if (controller.signal.aborted) return;

        const eventsOk = eventsResult.status === 'fulfilled';
        const sourcesOk = sourcesResult.status === 'fulfilled';
        const liveEvents = eventsOk ? eventsResult.value : [];
        const fixtures = fixturesResult.status === 'fulfilled' ? fixturesResult.value : [];
        const errors: string[] = [];
        const eventResolution = resolvePublicEvents({
          requestSucceeded: eventsOk,
          liveEvents,
          fixtures,
          fixturesEnabled: appEnv.fixturesEnabled,
          failureMessage: eventsOk ? undefined : describeError(eventsResult.reason, 'Public events unavailable'),
        });

        eventsState.value = eventResolution.events;
        sourcesState.value = sourcesOk ? sourcesResult.value : [];
        dataStatus.value = eventResolution.status;

        if (eventResolution.message) errors.push(`Events: ${eventResolution.message}`);
        if (!sourcesOk) {
          errors.push(`Sources: ${describeError(sourcesResult.reason, 'Public sources unavailable')}`);
        }
        dataError.value = errors.length ? errors.join(' ') : null;
      });

    return () => controller.abort();
  }, [publicApi]);

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const z = (value: number) => String(value).padStart(2, '0');
      clockState.value = `${z(now.getUTCHours())}:${z(now.getUTCMinutes())}:${z(now.getUTCSeconds())} UTC`;
    };
    updateClock();
    const timer = window.setInterval(updateClock, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === 'k') {
        event.preventDefault();
        commandPaletteOpenState.value = !commandPaletteOpenState.value;
      }
      if (event.key === 'Escape') {
        commandPaletteOpenState.value = false;
        confidenceGraphOpenState.value = false;
      }
      if (key === '?' && !event.metaKey && !event.ctrlKey) {
        commandPaletteOpenState.value = true;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div id="app" data-panel={narrowPanelState.value}>
      <TopBar />
      <FeedPanel />
      <MapPanel />
      <DetailPanel />
      <StatusBar />
      {commandPaletteOpenState.value ? <CommandPalette /> : null}
      {confidenceGraphOpenState.value ? <ConfidenceGraph /> : null}
      {legalGateOpenState.value ? <LegalGateModal /> : null}
    </div>
  );
}

function TopBar() {
  const [accountOpen, setAccountOpen] = useState(false);
  const authStatus = authStatusState.value;
  const authUser = authUserState.value;
  const viewerProfile = viewerProfileState.value;
  const legalCurrent = Boolean(viewerProfile?.profile.legal_acceptance_current);
  const authConfigured = authStatus !== 'disabled';
  const signedIn = authStatus === 'signed_in';
  const authTitle = !authConfigured
    ? 'Set VITE_CLERK_PUBLISHABLE_KEY to enable sign in'
    : signedIn
      ? `Account: ${authUser?.label || 'Signed in'}`
      : 'Sign in';

  return (
    <header id="topbar">
      <div id="logo" aria-label="CHAOC">
        <LogoMark />
        <span id="logo-wordmark">
          CHA<em>O</em>C
        </span>
      </div>
      <button
        id="cmd-bar-trigger"
        type="button"
        aria-label="Open command palette"
        onClick={() => {
          commandPaletteOpenState.value = true;
        }}
      >
        <Search size={14} />
        <span id="cmd-bar-placeholder">SEARCH EVENTS, PLACES, SOURCES...</span>
        <span id="cmd-bar-kbd">CMD K</span>
      </button>
      <div id="topbar-right">
        <div id="threshold-wrap">
          <span>CONF</span>
          <input
            type="range"
            id="threshold-slider"
            min={0}
            max={100}
            value={confidenceThresholdState.value}
            onInput={(event) => {
              confidenceThresholdState.value = Number((event.currentTarget as HTMLInputElement).value);
            }}
          />
          <span id="threshold-val" class={confidenceThresholdState.value ? 'active' : ''}>
            &gt;={confidenceThresholdState.value}%
          </span>
        </div>
        <button
          class="icon-btn"
          type="button"
          title="Confidence Graph"
          aria-label="Open confidence graph"
          onClick={() => (confidenceGraphOpenState.value = true)}
        >
          <Network size={14} />
        </button>
        <div class="account-wrap">
          <button
            class={`icon-btn account-btn ${signedIn ? 'signed-in' : ''}`}
            type="button"
            title={authTitle}
            aria-label={authTitle}
            aria-expanded={signedIn ? accountOpen : undefined}
            disabled={!authConfigured || authStatus === 'loading'}
            onClick={() => {
              if (!authConfigured) return;
              if (!signedIn) {
                void openSignIn();
                return;
              }
              setAccountOpen((value) => !value);
            }}
          >
            {authUser?.imageUrl ? <img class="account-avatar" src={authUser.imageUrl} alt="" /> : <User size={14} />}
          </button>
          {signedIn && accountOpen ? (
            <div class="account-menu">
              <div class="account-menu-hd">
                <span class="account-label">{authUser?.label || 'Signed in'}</span>
                <span class={`legal-mini ${legalCurrent ? 'ok' : 'blocked'}`}>
                  {legalCurrent ? 'LEGAL CURRENT' : 'LEGAL REQUIRED'}
                </span>
              </div>
              <button
                type="button"
                class="account-menu-item"
                onClick={() => {
                  setAccountOpen(false);
                  void openAccountPortal();
                }}
              >
                <User size={13} />
                Account portal
              </button>
              {!legalCurrent ? (
                <button
                  type="button"
                  class="account-menu-item"
                  onClick={() => {
                    setAccountOpen(false);
                    legalGateOpenState.value = true;
                  }}
                >
                  <ShieldCheck size={13} />
                  Review legal terms
                </button>
              ) : null}
              <button
                type="button"
                class="account-menu-item"
                onClick={() => {
                  setAccountOpen(false);
                  void signOut();
                }}
              >
                <LogOut size={13} />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function FeedPanel() {
  const events = filteredEventsState.value;
  return (
    <aside id="left-panel">
      <div id="feed-header">
        <div class="panel-label">INTEL FEED</div>
        <div id="filter-chips">
          {CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              class={`fchip ${filterState.value === category ? 'active' : ''}`}
              onClick={() => (filterState.value = category)}
            >
              {category.toUpperCase()}
            </button>
          ))}
        </div>
        <div id="sort-row">
          {SORTS.map((sort) => (
            <button
              key={sort}
              type="button"
              class={`sort-btn ${sortState.value === sort ? 'active' : ''}`}
              onClick={() => (sortState.value = sort)}
            >
              {sort.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div id="feed-list" data-testid="feed-list">
        {events.map((event) => (
          <EventCard key={event.id} event={event} selected={selectedEventState.value?.id === event.id} />
        ))}
      </div>
    </aside>
  );
}

function EventCard({ event, selected }: { event: ChaocEvent; selected: boolean }) {
  return (
    <button
      type="button"
      class={`ecard ${activityClass(event.activity)} ${selected ? 'selected' : ''}`}
      onClick={() => selectEvent(event)}
    >
      <div class="ecard-source-row">
        <div class="activity-pip" />
        <span class="ecard-source">{event.source}</span>
        <span class="ecard-conf">{pct(event.confidence)}%</span>
      </div>
      <div class="ecard-headline">{event.headline}</div>
      <div class="ecard-meta">
        <span class={`iptc-badge ${event.category}`}>{event.category.toUpperCase()}</span>
        <span class="ecard-loc">{event.location}</span>
        <span class="ecard-time">{event.timeAgo} ago</span>
      </div>
    </button>
  );
}

function MapPanel() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<ChaocMap | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!mapContainer.current) return;
    const map = new ChaocMap(mapContainer.current, {
      onHexSelected: selectHex,
      onResolutionChanged: (label) => {
        mapResolutionState.value = label;
      },
      onReadyChanged: setMapReady,
    });
    mapRef.current = map;
    void map.init();
    return () => {
      map.destroy();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const dispose = effect(() => {
      mapRef.current?.setViewState({
        events: eventsState.value,
        filter: filterState.value,
        confidenceThreshold: confidenceThresholdState.value,
        selectedHexId: selectedHexIdState.value,
      });
    });
    return dispose;
  }, []);

  useEffect(() => {
    const dispose = effect(() => {
      const target = mapTargetState.value;
      if (!target) return;
      mapRef.current?.flyTo(target.lng, target.lat, target.zoom);
      mapTargetState.value = null;
    });
    return dispose;
  }, []);

  return (
    <main id="map-area">
      <div id="map-init" class={mapReady ? 'fade' : ''}>
        <div class="init-hex-grid">
          {Array.from({ length: 15 }).map((_, index) => (
            <div class="init-hex" key={index} />
          ))}
        </div>
        <div class="init-label">{appEnv.maptilerKey || appEnv.appEnv === 'local' ? 'INITIALIZING GEO LAYER' : 'MAP KEY REQUIRED'}</div>
      </div>
      <div id="map" ref={mapContainer} />
      <div id="map-ctrl">
        <button class="mctrl" type="button" title="Zoom in" aria-label="Zoom in" onClick={() => mapRef.current?.zoomIn()}>
          <Plus size={14} />
        </button>
        <button class="mctrl" type="button" title="Zoom out" aria-label="Zoom out" onClick={() => mapRef.current?.zoomOut()}>
          <Minus size={14} />
        </button>
        <button class="mctrl" type="button" title="Recenter" aria-label="Recenter map" onClick={() => mapRef.current?.recenter()}>
          <Crosshair size={14} />
        </button>
      </div>
      <div id="map-legend">
        <div class="leg-title">HEX SIGNALS</div>
        <div class="leg-row">
          <div class="leg-swatch activity" />
          <span class="leg-label">ACTIVITY</span>
        </div>
        <div class="leg-row">
          <div class="leg-swatch volatility" />
          <span class="leg-label">VOLATILITY</span>
        </div>
        <div class="leg-row">
          <div class="leg-swatch flag" />
          <span class="leg-label">USER FLAG</span>
        </div>
      </div>
    </main>
  );
}

function DetailPanel() {
  const selected = selectedEventState.value;
  const events = selectedHexEventsState.value;
  const primary = selected || events[0] || null;
  const tickers = primary ? OSE_BY_CATEGORY[primary.category] || OSE_BY_CATEGORY.economy : [];
  const selectedLabel = selectedHexIdState.value?.startsWith('EVENT:')
    ? `EVENT ${selectedHexIdState.value.replace('EVENT:', '').slice(0, 18)}`
    : selectedHexIdState.value
      ? `HEX ${selectedHexIdState.value.slice(0, 18)}`
      : 'SELECT A HEX OR EVENT';
  return (
    <aside id="right-panel">
      <div id="detail-header">
        <div class="panel-label">EVENT DETAIL</div>
        <div id="detail-hex">{selectedLabel}</div>
        <div id="detail-coord">{primary ? `${primary.lat.toFixed(3)}, ${primary.lng.toFixed(3)} - ${events.length || 1} event${(events.length || 1) === 1 ? '' : 's'}` : ''}</div>
        {primary ? (
          <div id="signal-row">
            <div class="signal-pill activity">
              <span class="sig-icon" />
              ACT {pct(primary.activity)}%
            </div>
            <div class="signal-pill volatility">
              <span class="sig-icon" />
              VOL {pct(primary.volatility)}%
            </div>
          </div>
        ) : null}
      </div>
      <div id="detail-scroll">
        {!primary ? (
          <div id="detail-empty">
            <div class="empty-hex-cluster">
              {Array.from({ length: 9 }).map((_, index) => (
                <div class="ehex" key={index} />
              ))}
            </div>
            <div class="empty-msg">SELECT A HEX CELL<br />TO VIEW PUBLIC BASELINE</div>
          </div>
        ) : (
          <div id="detail-content">
            <div class="section-hd">PUBLIC BASELINE EVENTS</div>
            <div id="detail-events">
              {(events.length ? events : [primary]).map((event) => (
                <button type="button" class="detail-event" key={event.id} onClick={() => selectEvent(event)}>
                  <div class="de-src">
                    <span>{event.source}</span>
                    <span>{pct(event.confidence)}%</span>
                  </div>
                  <div class="de-hl">{event.headline}</div>
                  <div class="de-bar">
                    <div class="de-bar-fill" style={{ width: `${pct(event.activity)}%` }} />
                  </div>
                </button>
              ))}
            </div>
            <div id="ose-wrap">
              <div class="ose-hd">
                <span class="ose-hd-text">MARKET CONTEXT - READ ONLY</span>
                <span class="ose-caret">W0</span>
              </div>
              <div class="market-safety">Context only. CHAOC is not financial advice and no execution flow is enabled.</div>
              {tickers.map((ticker) => (
                <div class="ose-ticker" key={`${ticker.platform}-${ticker.name}`}>
                  <span class={`ose-plat ${ticker.platform}`}>{ticker.platform.toUpperCase()}</span>
                  <span class="ose-name">{ticker.name}</span>
                  <div class="ose-price">
                    {ticker.price}
                    <span class={`ose-change ${ticker.up ? 'up' : 'down'}`}>{ticker.change}</span>
                  </div>
                </div>
              ))}
            </div>
            <div id="onto-section">
              <div class="onto-label">VISIBLE EVIDENCE</div>
              <div class="onto-tags">
                <span class="onto-tag">PUBLIC BASELINE</span>
                <span class="onto-tag">{primary.category.toUpperCase()}</span>
                {primary.sourceServiceId ? <span class="onto-tag">{primary.sourceServiceId}</span> : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function StatusBar() {
  const authStatus = authStatusState.value;
  const profileStatus = viewerProfileStatusState.value;
  const authControl = authControlModel(authStatus, profileStatus);
  const authError = authErrorState.value || viewerProfileErrorState.value;

  return (
    <footer id="statusbar">
      <div class="sitem">
        <div class={`slive-dot ${dataStatus.value}`} />
        <span class="sitem-val">{publicDataStatusLabel(dataStatus.value)}</span>
      </div>
      <div class="sitem">
        <span>SOURCES</span>
        <span class="sitem-val">{sourcesState.value.length || 'PUBLIC'}</span>
      </div>
      <div class="sitem">
        <span>EVENTS</span>
        <span class="sitem-val">{filteredEventsState.value.length}</span>
      </div>
      <div class="sitem">
        <span>HEX</span>
        <span class="sitem-val">{mapResolutionState.value}</span>
      </div>
      {authControl.interactive ? (
        <button
          class={`sbtn auth-status ${authStatus === 'signed_in' ? 'signed-in' : ''}`}
          type="button"
          onClick={() => {
            if (authControl.action === 'review_legal') {
              legalGateOpenState.value = true;
              return;
            }
            void openSignIn();
          }}
        >
          {authControl.label}
        </button>
      ) : (
        <span class={`sbtn auth-status ${authStatus === 'signed_in' ? 'signed-in' : ''}`}>
          {authControl.label}
        </span>
      )}
      <button class="sbtn" type="button" onClick={() => (confidenceGraphOpenState.value = true)}>
        CONFIDENCE GRAPH
      </button>
      {dataError.value ? <div class="sitem status-error">{dataError.value}</div> : null}
      {authError ? <div class="sitem status-error">{authError}</div> : null}
      <div class="sright">
        <div class="sitem">
          <span>API</span>
          <span class="sitem-val">{appEnv.appEnv}</span>
        </div>
        <div id="clock">{clockState.value}</div>
      </div>
    </footer>
  );
}

function LegalGateModal() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const profile = viewerProfileState.value;
  const busy = viewerProfileStatusState.value === 'loading';
  const error = viewerProfileErrorState.value;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();

    const handleCancel = (event: Event) => {
      if (!viewerProfileState.value?.profile.legal_acceptance_current) {
        event.preventDefault();
      }
    };
    const handleClose = () => {
      if (viewerProfileState.value?.profile.legal_acceptance_current) {
        legalGateOpenState.value = false;
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleClose);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleClose);
    };
  }, []);

  return (
    <dialog id="legal-modal" ref={dialogRef} aria-label="Legal acceptance required">
      <div id="legal-box">
        <div id="legal-hd">
          <div>
            <span class="panel-label">ACCOUNT GATE</span>
            <h2>Legal acceptance required</h2>
          </div>
          {profile?.profile.legal_acceptance_current ? (
            <button id="legal-close" type="button" aria-label="Close legal gate" onClick={() => dialogRef.current?.close()}>
              <X size={16} />
            </button>
          ) : null}
        </div>
        <div class="legal-copy">
          <p>CHAOC analysis is probabilistic, incomplete, and only as good as the visible source set behind it.</p>
          <p>Market context is read-only information. CHAOC is not a financial advisor and does not provide trade execution.</p>
          <p>Do not upload, share, or reuse paid or restricted source data unless your source licenses allow it.</p>
        </div>
        <div class="legal-version-row">
          <span>Terms {profile?.legal.terms_version || 'pending'}</span>
          <span>Privacy {profile?.legal.privacy_version || 'pending'}</span>
        </div>
        {error ? <div class="legal-error">{error}</div> : null}
        <div id="legal-actions">
          <button class="legal-secondary" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
          <button class="legal-primary" type="button" disabled={!profile || busy} onClick={() => void acceptCurrentLegal()}>
            {busy ? 'Saving...' : 'Accept and continue'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function CommandPalette() {
  const [query, setQuery] = useState('');
  const dialogRef = useDialogFocus<HTMLDivElement>(() => (commandPaletteOpenState.value = false));
  const q = query.trim().toLowerCase();
  const eventMatches = filteredEventsState.value
    .filter((event) => !q || `${event.headline} ${event.source} ${event.location}`.toLowerCase().includes(q))
    .slice(0, 6);
  const placeMatches = Object.entries(NAMED_GEOS)
    .filter(([key, place]) => !q || key.includes(q) || place.label.toLowerCase().includes(q))
    .slice(0, 5);

  return (
    <div id="cmd-palette" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Command palette" tabIndex={-1}>
      <div id="cmd-input-wrap">
        <Search size={15} />
        <input
          type="text"
          id="cmd-input"
          autoFocus
          value={query}
          placeholder="SEARCH EVENTS, PLACES, SOURCES..."
          onInput={(event) => setQuery((event.currentTarget as HTMLInputElement).value)}
        />
        <button id="cmd-esc" type="button" onClick={() => (commandPaletteOpenState.value = false)}>
          ESC
        </button>
      </div>
      <div id="cmd-groups">
        <div class="cmd-group-label">EVENTS</div>
        {eventMatches.map((event) => (
          <button
            type="button"
            class="cmd-result"
            key={event.id}
            onClick={() => {
              selectEvent(event);
              commandPaletteOpenState.value = false;
            }}
          >
            <span class="cmd-type">{event.category.toUpperCase()}</span>
            <span class="cmd-text">{event.headline}</span>
            <span class="cmd-sub">{event.location}</span>
          </button>
        ))}
        <div class="cmd-group-label">PLACES</div>
        {placeMatches.map(([key, place]) => (
          <button
            type="button"
            class="cmd-result"
            key={key}
            onClick={() => {
              mapTargetState.value = { lng: place.lng, lat: place.lat, zoom: place.zoom };
              commandPaletteOpenState.value = false;
            }}
          >
            <span class="cmd-type">PLACE</span>
            <span class="cmd-text">{place.label}</span>
            <span class="cmd-sub">{place.lat.toFixed(2)}, {place.lng.toFixed(2)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ConfidenceGraph() {
  const dialogRef = useDialogFocus<HTMLDivElement>(() => (confidenceGraphOpenState.value = false));

  return (
    <div id="cgraph-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Confidence graph" tabIndex={-1}>
      <div id="cgraph-box">
        <div id="cgraph-hd">
          <span id="cgraph-title">CONFIDENCE GRAPH - PUBLIC BASELINE PREVIEW</span>
          <button id="cgraph-close" type="button" aria-label="Close confidence graph" onClick={() => (confidenceGraphOpenState.value = false)}>
            <X size={16} />
          </button>
        </div>
        <div id="cgraph-canvas">
          <svg id="cgraph-svg" viewBox="0 0 720 360" aria-hidden="true">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M40 0L0 0 0 40" fill="none" stroke="#1C1C24" strokeWidth="0.4" />
              </pattern>
            </defs>
            <rect width="720" height="360" fill="url(#grid)" />
            <line x1="150" y1="90" x2="350" y2="180" stroke="#2C1E3A" strokeDasharray="3 3" />
            <line x1="350" y1="180" x2="560" y2="110" stroke="#2C1E3A" strokeDasharray="3 3" />
            <line x1="350" y1="180" x2="430" y2="285" stroke="#1E1E2A" />
            <circle cx="150" cy="90" r="22" fill="#8C3A24" opacity="0.9" />
            <circle cx="560" cy="110" r="18" fill="#6F311F" opacity="0.85" />
            <polygon points="350,152 380,205 320,205" fill="#6C3D91" opacity="0.9" />
            <polygon points="430,258 455,285 430,312 405,285" fill="#2D6FBF" opacity="0.85" />
            <text x="150" y="130" text-anchor="middle" fill="#5A5A6A" font-size="12">SOURCE</text>
            <text x="560" y="148" text-anchor="middle" fill="#5A5A6A" font-size="12">WIRE</text>
            <text x="350" y="230" text-anchor="middle" fill="#5A5A6A" font-size="12">PROPOSITION</text>
            <text x="430" y="335" text-anchor="middle" fill="#5A5A6A" font-size="12">ENTITY</text>
          </svg>
        </div>
        <div id="cgraph-legend">
          <span>W0 renders public baseline graph preview only.</span>
          <span class="legend-note">Viewer-scoped evidence lands in W2.</span>
        </div>
      </div>
    </div>
  );
}

function LogoMark() {
  return (
    <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <polygon points="13,1 24,7 24,19 13,25 2,19 2,7" stroke="#C0492B" strokeWidth="1.1" fill="none" />
      <polygon points="13,5 20,9 20,17 13,21 6,17 6,9" stroke="#C0492B" strokeWidth="0.5" fill="none" opacity="0.35" />
      <circle cx="13" cy="13" r="1.8" fill="#C0492B" />
    </svg>
  );
}
